import { Badge, Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { upperFirst } from "lodash-es";
import {
	Globe2,
	Linkedin,
	Mail,
	MapPin,
	MessageCircle,
	Phone,
} from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { AdvisorContactLine } from "web_myplann/Advisor/Components/AdvisorContactLine";
import { AdvisorIntroduction } from "web_myplann/Advisor/Components/AdvisorIntroduction";
import { AdvisorMainBadge } from "web_myplann/Advisor/Components/AdvisorMainBadge";
import type { AdvisorSpecializationKey } from "web_myplann/Advisor/Types/AdvisorSpecializationKey";
import { getAdvisorExternalUrl } from "web_myplann/Advisor/Utils/getAdvisorExternalUrl";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InitialsAvatar } from "web_myplann/Shared/Components/InitialsAvatar";
import { handlePhoneClick } from "web_myplann/Shared/Utils/openExternalApps";
import { device } from "web_myplann/Utils/device";
import { getMapsLink } from "web_myplann/Utils/getMapsLink";

const R = Resources.Advisor;

type Props = {
	id: string;
	name: string;
	job?: string;
	specializations: AdvisorSpecializationKey[];
	hspName?: string;
	phone?: string;
	email?: string;
	address?: string;
	linkedin?: string;
	website?: string;
	description?: string;
	photo?: string;
	conversationId?: number;
	unreadCount: number;
	showMainBadge?: boolean;
};

export const AdvisorCard: FC<Props> = ({
	id,
	name,
	job,
	specializations,
	hspName,
	phone,
	email,
	address,
	linkedin,
	website,
	description,
	photo,
	conversationId,
	unreadCount,
	showMainBadge,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();
	const hasGoalsRight = useGoalsRightOptional();
	const specializationText = upperFirst(
		specializations.map((key) => t(R.specializations[key])).join(", "),
	);

	const openChat = () => {
		if (hasGoalsRight) {
			navigate(
				conversationId
					? Routing.Chat.Detail.path(String(conversationId))
					: Routing.Chat.Detail.path("new", id),
			);
			return;
		}
		device.navigateChatRequest(String(conversationId ?? ""), name, id);
	};

	return (
		<Stack bgcolor={theme.palette.background.paper} pb={theme.spacingTokens.lg}>
			<Stack
				px={theme.spacingTokens.lg}
				pt={theme.spacingTokens.lg}
				alignItems="center"
			>
				<InitialsAvatar name={name} photo={photo} mb={theme.spacingTokens.md} />

				<Stack
					direction="row"
					alignItems="center"
					justifyContent="center"
					flexWrap="wrap"
					gap={theme.spacingTokens.sm}
				>
					<Typography variant="h3" textAlign="center">
						{name}
					</Typography>
					{showMainBadge && <AdvisorMainBadge />}
				</Stack>

				{job && (
					<Typography
						variant="body2"
						color={theme.palette.grey[500]}
						mt={theme.spacingTokens.xs}
						textAlign="center"
						sx={{ overflowWrap: "anywhere" }}
					>
						{job}
					</Typography>
				)}

				{specializationText && (
					<Typography variant="hint" textAlign="center">
						{specializationText}
					</Typography>
				)}

				{hspName && (
					<Typography
						variant="bodyMedium"
						color={theme.palette.grey[500]}
						mt={theme.spacingTokens.sm}
						textAlign="center"
						sx={{ overflowWrap: "anywhere" }}
					>
						{hspName}
					</Typography>
				)}

				<Stack
					gap={theme.spacingTokens.sm}
					alignItems="center"
					mt={theme.spacingTokens.lg}
					mb={theme.spacingTokens.xl}
					width="100%"
				>
					{phone && <AdvisorContactLine icon={Phone} text={phone} />}
					{email && (
						<AdvisorContactLine
							icon={Mail}
							text={email}
							href={`mailto:${email}`}
							ariaLabel={t(R.emailAria, { email })}
						/>
					)}
					{address && (
						<AdvisorContactLine
							icon={MapPin}
							text={address}
							href={getMapsLink(address)}
							ariaLabel={t(R.addressAria, { address })}
						/>
					)}
					{linkedin && (
						<AdvisorContactLine
							icon={Linkedin}
							text={linkedin}
							href={getAdvisorExternalUrl(linkedin)}
							ariaLabel={t(R.linkedinAria)}
						/>
					)}
					{website && (
						<AdvisorContactLine
							icon={Globe2}
							text={website}
							href={getAdvisorExternalUrl(website)}
							ariaLabel={t(R.websiteAria)}
						/>
					)}
				</Stack>

				<Box
					display="grid"
					gridAutoFlow="column"
					gridAutoColumns="1fr"
					gap={theme.spacingTokens.sm}
					width="100%"
				>
					{phone && (
						<Button
							variant="primary"
							startIcon={<Phone size={theme.iconSize.sm} />}
							onClick={() => handlePhoneClick(phone)}
						>
							{t(R.call)}
						</Button>
					)}
					<Badge
						badgeContent={unreadCount}
						color="error"
						max={99}
						variant="button"
					>
						<Button
							variant="tertiary"
							startIcon={<MessageCircle size={theme.iconSize.sm} />}
							onClick={openChat}
							fullWidth
						>
							{t(R.messages)}
						</Button>
					</Badge>
				</Box>
			</Stack>

			{description && <AdvisorIntroduction text={description} />}
		</Stack>
	);
};
